<template>
    <div class="feedback">
        <div class="feedback-info" :class="{ clickable: canViewContent }">
            <div class="users-container" @click="openAsks()">
                <user
                    :user="feedback.valuer"
                    :date="feedback.updated_at"
                    :archived_at="feedback.archived_at"
                />
                <img
                    class="icon"
                    :src="require('@/assets/icons/chevron.svg')"
                />
                <user :user="feedback.evaluated" class="user-right" />
            </div>
            <div
                class="d-flex align-items-center justify-content-end w-100 mr-2 p-2"
            >
                <div class="badge-group">
                    <b-badge
                        class="pill pill-50"
                        pill
                        variant="outline-primary"
                        v-if="feedback.category"
                    >
                        <span class="span-pill">
                            <improvement
                                class="icon-pill"
                                v-if="feedback.category === 'improvement'"
                                color="#009bdb"
                            />
                            <positive
                                class="icon-pill"
                                v-if="feedback.category === 'positive'"
                                color="#009bdb"
                            />
                            {{ $t(`talk.request.${feedback.category}.name`) }}
                        </span>
                    </b-badge>
                    <b-badge
                        class="pill"
                        :class="{ [`pill-${feedbackStatus}`]: true }"
                        pill
                    >
                        <span>
                            {{ $t('talk.feedback.status.' + feedbackStatus) }}
                        </span>
                    </b-badge>
                </div>
                <b-dropdown
                    v-if="showContextMenu"
                    dropleft
                    no-caret
                    size="sm"
                    variant="white"
                    class="ml-2"
                >
                    <template slot="button-content">
                        <i class="fas fa-ellipsis-v text-muted" />
                    </template>
                    <b-dropdown-item
                        v-if="isNotArchived"
                        @click="onClickUpdateArchivedAt(true)"
                        >{{ $t('form.button.archive') }}</b-dropdown-item
                    >
                    <b-dropdown-item
                        v-else
                        @click="onClickUpdateArchivedAt(false)"
                        >{{ $t('form.button.unarchive') }}</b-dropdown-item
                    >
                </b-dropdown>
            </div>
        </div>
        <div
            :class="{ 'collapse-container': showAsks }"
            class="collapse-default"
            v-if="canViewContent"
        >
            <b-collapse v-model="showAsks" :id="`collapse-${feedback.id}`">
                <div class="asks-container spontaneous" v-if="isSpontaneous">
                    <answer
                        v-for="answer in feedback.answers"
                        :key="`answer-${answer.id}`"
                        :answer="answer"
                    />
                </div>
                <div
                    class="asks-container not-spontaneous"
                    v-if="!isSpontaneous"
                >
                    <category-select
                        v-if="canViewAnswerForm"
                        class="d-block m-3"
                        :bold="true"
                        :lg="false"
                        :md="true"
                        :model.sync="$v.category.$model"
                    />
                    <ask
                        v-for="(ask, index) in feedback.askings"
                        :key="`ask-${ask.id}`"
                        :ask="ask"
                        :type="type"
                        :state="checkState(index)"
                        :canViewForm="canViewAnswerForm"
                        @answerChanged="
                            $v.feedback.askings.$each.$iter[
                                index
                            ].pivot.content.$model = $event
                        "
                    />
                </div>

                <chat
                    :comments="feedback.comments"
                    :feedbackId="feedback.id"
                    v-if="type !== 'pending'"
                />
                <div class="send-button-container" v-if="canViewAnswerForm">
                    <c-button-loader
                        type="submit"
                        variant="primary"
                        :loader="isLoading"
                        :disabled="$v.feedback.$invalid ? true : isLoading"
                        @click="sendAnswers"
                        >{{ $t('form.button.send') }}</c-button-loader
                    >
                </div>
            </b-collapse>
        </div>
    </div>
</template>

<script>
import { EventBuzz } from '@/main'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import notification, { success } from '@/lib/notification'
import { Improvement, Positive, CategorySelect } from '@/views/talk/components'
import CButtonLoader from '@/views/components/button-loader'
import { User, Answer, Chat, Ask } from './children'
import { proceedWithConfirmation } from '@/lib/sweet-alert'
import { saveAnswers, archiveFeedback, unarchiveFeedback } from './children/api'

notification.init()

export default {
    name: 'Feedback',
    mixins: [validationMixin],
    components: {
        User,
        Answer,
        Chat,
        Ask,
        CButtonLoader,
        Improvement,
        Positive,
        CategorySelect,
    },
    props: {
        feedback: Object,
        type: String,
        role: String,
    },
    validations: {
        feedback: {
            askings: {
                $each: {
                    pivot: {
                        content: {
                            required,
                        },
                    },
                },
            },
        },
        category: {},
    },
    data() {
        return {
            showAsks: false,
            isLoading: false,
            category: null,
        }
    },
    methods: {
        openAsks() {
            if (this.canViewContent) {
                this.showAsks = !this.showAsks
            }
        },
        checkState(index) {
            const field = this.$v.feedback.askings.$each.$iter[index].pivot
                .content

            return field.$dirty ? !field.$invalid : null
        },
        sendAnswers() {
            if (this.$v.feedback.$invalid) {
                return
            }
            const form = this.parseFormFromFeedback()
            this.isLoading = true
            saveAnswers(this.feedback.id, form)
                .then(() => this.onFeedbackSuccessfullyUpdated())
                .catch(e => console.log(e))
                .finally(() => (this.isLoading = false))
        },
        parseFormFromFeedback() {
            let answers = {}
            this.feedback.askings.forEach(a => {
                answers = {
                    ...answers,
                    [a.id]: {
                        content: a.pivot.content,
                    },
                }
            })
            return { answers, category: this.category }
        },
        onClickUpdateArchivedAt(archive) {
            proceedWithConfirmation({
                onConfirm: () => {
                    if (archive) {
                        return archiveFeedback(this.feedback.id)
                    }
                    return unarchiveFeedback(this.feedback.id)
                },
                callback: () => this.onFeedbackSuccessfullyUpdated(),
            })
        },
        onFeedbackSuccessfullyUpdated() {
            success()
            this.emitUpdateEvents()
        },
        emitUpdateEvents() {
            this.$emit('reloadFeedbacks')
            EventBuzz.$emit('update-talk-summary')
        },
    },
    computed: {
        isNotArchived() {
            return this.feedback.archived_at == null
        },
        isSpontaneous() {
            return this.feedback.is_spontaneous
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        showContextMenu() {
            return this.canViewContent && this.role == 'user'
        },
        canViewContent() {
            return (
                this.feedback.evaluated.id === this.currentUser.id ||
                this.feedback.valuer.id === this.currentUser.id
            )
        },
        feedbackStatus() {
            if (!this.isSpontaneous && this.feedback.status === 'sent') {
                return 'requested'
            }
            if (!this.isSpontaneous && this.feedback.status === 'pending') {
                return 'pending'
            }
            return 'sent'
        },
        canViewAnswerForm() {
            return (
                this.type === 'pending' &&
                this.currentUser.id === this.feedback.valuer_id
            )
        },
    },
}
</script>

<style lang="sass" scoped>
$gray: #ececec
$green: #359C61
$orange: #FB781F
$red: #DE5B5B
$primary: #009bdb
.clickable
    cursor: pointer !important

.clickable
    cursor: pointer

.feedback
    width: 100%
    background: #fff
    border: 1px solid $gray
    border-radius: 6px
    margin-bottom: 10px
    display: flex
    transition: .5s ease all
    box-shadow: 2px 2px #ebebeb
    -webkit-box-shadow: 2px 2px #ebebeb
    justify-content: center
    flex-direction: column
    .feedback-info
        display: flex
        justify-content: center
        align-items: center
        position: relative
        .users-container
            display: flex
            align-items: center
            flex-direction: column
            padding: 20px
        .badge-group
            display: flex
            height: 100%
            flex-direction: column
            justify-content: space-around
            align-items: center
            .pill
                font-size: 12px
                font-weight: 400
                display: flex
                justify-content: center
                align-items: center
                min-width: 70px
                background: none
                border: 1px solid
                height: 25px
                padding-top: 0
                padding-bottom: 0
                margin-bottom: 20px
                .span-pill
                    min-height: 25px
                    display: flex
                    justify-content: center
                    align-items: center
            .pill-50
                color: $primary
                .icon-pill
                    display: inline-block
                    margin-right: 5px
                ::v-deep
                    svg
                        width: 15px
                        height: 15px
            .pill-requested
                border-color: $orange
                color: $orange
            .pill-pending
                border-color: $red
                color: $red
            .pill-sent
                border-color: $green
                color: $green
    .collapse-default
        transition: 0.5s ease all
        padding: 0 20px
    .collapse-container
        padding: 20px
    .icon
        margin: 20px
        transform: rotate(90deg)
    .asks-container
        border-top: 1px solid $gray
        padding: 20px 10px
        padding-top: 0
    .send-button-container
        display: flex
        justify-content: flex-end
        padding: 0 20px
        button
            width: 90px

@media (min-width: 768px)
    .feedback
        .feedback-info
            flex-direction: row
            justify-content: space-between
            .users-container
                flex-direction: row
                justify-content: flex-start
            .badge-group
                flex-direction: row
                .badge
                    margin-bottom: 0
                    margin-right: 5px
        .icon
            transform: rotate(0)
        .asks-container
            padding: 30px 10px
</style>
